import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/v2/Toast";
import {
  NOTIFICATION_PREF_DEFAULTS,
  useNotificationPrefs,
  useUpdateNotificationPrefs,
  type NotificationPrefs,
} from "@/lib/notification-prefs";

type Prefs = Required<NotificationPrefs>;

export function AccountNotifications() {
  const toast = useToast();
  const { data, isLoading } = useNotificationPrefs();
  const update = useUpdateNotificationPrefs();
  const [prefs, setPrefs] = useState<Prefs>(NOTIFICATION_PREF_DEFAULTS);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setPrefs({ ...NOTIFICATION_PREF_DEFAULTS, ...data });
      setDirty(false);
    }
  }, [data]);

  const set = <K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    try {
      await update.mutateAsync(prefs);
      setDirty(false);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(
        "Couldn't save",
        err instanceof Error ? err.message : "Try again.",
      );
    }
  };

  return (
    <div className="space-y-6">
      <Section
        title="Email"
        description="Sent to your sign-in email. You'll always get critical security alerts."
      >
        <Toggle
          label="New booking received"
          desc="Hits your inbox the moment a customer submits a booking."
          checked={prefs.emailNewBooking}
          onChange={(v) => set("emailNewBooking", v)}
        />
        <Toggle
          label="Payment receipt uploaded"
          desc="So you can verify quickly without opening the app."
          checked={prefs.emailPaymentReceipt}
          onChange={(v) => set("emailPaymentReceipt", v)}
        />
        <Toggle
          label="Weekly summary email"
          desc="Monday morning recap of last week's bookings, revenue, and top services."
          checked={prefs.emailWeeklyDigest}
          onChange={(v) => set("emailWeeklyDigest", v)}
        />
      </Section>

      <Section title="In-app" description="The bell badge in the topbar.">
        <Toggle
          label="New booking received"
          checked={prefs.inAppNewBooking}
          onChange={(v) => set("inAppNewBooking", v)}
        />
        <Toggle
          label="Payment receipt uploaded"
          checked={prefs.inAppPaymentReceipt}
          onChange={(v) => set("inAppPaymentReceipt", v)}
        />
      </Section>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={!dirty || isLoading || update.isPending}
        >
          {update.isPending ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card">
      <div className="px-5 py-4 border-b border-border">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <div className="divide-y">{children}</div>
    </section>
  );
}

function Toggle({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 px-5 py-3 cursor-pointer">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </label>
  );
}
